/**
 * Checkout API — MOCK implementation.
 *
 * Every function here is a stand-in for a backend endpoint and is the single
 * place to swap once the Python backend lands:
 *   getCart()            → GET  /cart
 *   getShippingMethods() → GET  /checkout/shipping?country=
 *   quote()              → POST /checkout/quote     (authoritative totals)
 *   createOrder()        → POST /checkout/order      (pending order + provider session)
 *
 * Totals MUST be computed server-side in production (markup, discounts,
 * shipping, tax, currency, commission tracking). The maths below only exists so
 * the UI is demonstrable now.
 */

import type {
  CartItem,
  CreateOrderInput,
  CreateOrderResult,
  Quote,
  ShippingMethod,
} from "./types";

/** Presentment currency — later derived from geoloc / user choice. */
export const CURRENCY = "EUR";

// Demo bag so the flow is visible while the real cart/backend is pending.
const MOCK_CART: CartItem[] = [
  {
    id: "i1",
    title: "Lace Underwire Corset",
    image:
      "https://cdn.shopify.com/s/files/1/0293/9277/files/11-12-25_S2_14_S774711FN_Wine_ZSR_KP_EO_11-18-29_10904_PXF.jpg",
    variant: "Size S · Wine",
    unitPrice: 39.9,
    qty: 1,
  },
  {
    id: "i2",
    title: "Feather-Trim Mini Dress",
    image:
      "https://cdn.shopify.com/s/files/1/0293/9277/files/12-09-25_S1_45_ZDMKLLP71822_Fuchsia_AB_NL_14-58-04_100649_PXF.jpg",
    variant: "Size M · Fuchsia",
    unitPrice: 59.0,
    qty: 2,
  },
];

const SHIPPING_METHODS: ShippingMethod[] = [
  { id: "standard", label: "Standard", eta: "5–8 business days", price: 0 },
  { id: "express", label: "Express", eta: "2–3 business days", price: 9.9 },
  { id: "priority", label: "Priority", eta: "1–2 business days", price: 19.9 },
];

// Mock discount codes (percentage + absolute) — see IDEA §Sconti.
const DISCOUNTS: Record<string, { type: "pct" | "abs"; value: number }> = {
  RUBYN10: { type: "pct", value: 10 },
  WELCOME15: { type: "pct", value: 15 },
  MINUS20: { type: "abs", value: 20 },
};

const delay = <T>(value: T, ms = 120): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export function getCart(): Promise<CartItem[]> {
  // TODO(backend): GET /cart for the current (guest) session.
  return delay(MOCK_CART);
}

export function getShippingMethods(country: string): Promise<ShippingMethod[]> {
  // TODO(backend): GET /checkout/shipping?country=${country} — rates depend on
  // the destination; the mock returns the same flat set regardless.
  void country;
  return delay(SHIPPING_METHODS);
}

export function isDiscountValid(code: string): boolean {
  return Boolean(DISCOUNTS[code.trim().toUpperCase()]);
}

export function quote(input: {
  items: CartItem[];
  shippingMethodId: string | null;
  discountCode: string | null;
}): Promise<Quote> {
  // TODO(backend): POST /checkout/quote — server is authoritative for every figure.
  const subtotal = input.items.reduce((s, i) => s + i.unitPrice * i.qty, 0);

  let code: string | null = null;
  let discountAmount = 0;
  if (input.discountCode) {
    const d = DISCOUNTS[input.discountCode.trim().toUpperCase()];
    if (d) {
      code = input.discountCode.trim().toUpperCase();
      discountAmount =
        d.type === "pct" ? (subtotal * d.value) / 100 : Math.min(d.value, subtotal);
    }
  }

  const method = SHIPPING_METHODS.find((m) => m.id === input.shippingMethodId) ?? null;
  const shippingAmount = method?.price ?? 0;
  const taxAmount = 0; // no VAT yet (IDEA §Pagamenti)
  const total = Math.max(0, subtotal - discountAmount) + shippingAmount + taxAmount;

  return delay({
    currency: CURRENCY,
    items: input.items,
    subtotal,
    discount: { code, amount: discountAmount },
    shipping: { methodId: method?.id ?? null, amount: shippingAmount },
    taxAmount,
    total,
  });
}

export function createOrder(input: CreateOrderInput): Promise<CreateOrderResult> {
  // TODO(backend): POST /checkout/order — create a pending order + provider
  // session (PayPal Orders API / YooKassa / crypto gateway) and return the
  // approval URL. The mock returns no URL so the UI shows a demo confirmation.
  return delay({ orderId: `mock_${input.rail}_order`, approvalUrl: "" });
}

/** Currency formatter for display. */
export function formatMoney(amount: number, currency: string = CURRENCY): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
}
